import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../ui/modal.jsx';
import { ApiError, api } from '../../api/index.js';

const EMPTY = {
  displayName: '', rank: '', position: '', callsign: '', hasDevice: false,
  homeNodeOnly: false, dmrId: '',
};

// Round 65 (2026-05-30) — DMR ID usable range.
const DMR_ID_MIN = 1;
const DMR_ID_MAX = 16776415;

// Round 331 (2026-06-22) — find lowest unused DMR ID starting from
// `startFrom` (inclusive). Returns null if the entire range is full.
// Used by the inline «Next free» button below the input field.
function findNextFreeDmrId(usageList, startFrom = 1) {
  const used = new Set(usageList.map((p) => p.dmrId));
  let n = Math.max(DMR_ID_MIN, startFrom | 0);
  while (n <= DMR_ID_MAX) {
    if (!used.has(n)) return n;
    n++;
  }
  return null;
}

export function PersonFormModal({ open, mode, unitName, initial, onClose, onSave }) {
  const isCreate = mode === 'create';
  const [values, setValues] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Round 331 (2026-06-22) — occupied DMR IDs across all personnel.
  // Fetched lazily on modal open so the visible list inside the form
  // is fresh (Personnel page state may be stale by the time operator
  // returns to edit an existing person). The display list shows the
  // set sorted by DMR ID + supports a text filter (callsign substring
  // OR ID substring). Click on a row → autofill input. «Next free»
  // button computes the lowest unused ID client-side.
  const [dmrUsage, setDmrUsage] = useState({ list: [], loading: false });
  const [dmrFilter, setDmrFilter] = useState('');

  useEffect(() => {
    if (!open) return;
    setError(null);
    setBusy(false);
    setDmrFilter('');
    setValues(
      isCreate
        ? { ...EMPTY }
        : {
            displayName: initial?.displayName || '',
            rank: initial?.rank || '',
            position: initial?.position || '',
            callsign: initial?.callsign || '',
            hasDevice: !!initial?.hasDevice,
            homeNodeOnly: !!initial?.homeNodeOnly,
            // Round 65 — pre-fill existing DMR ID on edit; blank on create
            // (server auto-assigns the lowest free integer).
            dmrId: initial?.dmrId != null ? String(initial.dmrId) : '',
          },
    );
    // Round 331 — fetch DMR usage table. Cancellable on close.
    let cancelled = false;
    setDmrUsage({ list: [], loading: true });
    api.persons
      .list()
      .then((res) => {
        if (cancelled) return;
        const list = (res?.persons || [])
          .filter((p) => p?.dmrId != null && Number.isFinite(Number(p.dmrId)))
          .map((p) => ({
            id: p.id,
            callsign: p.callsign || p.displayName || `#${p.id}`,
            displayName: p.displayName || '',
            dmrId: Number(p.dmrId),
          }))
          .sort((a, b) => a.dmrId - b.dmrId);
        setDmrUsage({ list, loading: false });
      })
      .catch(() => {
        if (!cancelled) setDmrUsage({ list: [], loading: false });
      });
    return () => {
      cancelled = true;
    };
  }, [open, isCreate, initial]);

  // Round 331 — derive conflict / own-row / filtered-list / next-free.
  // Full list rendered — visible window capped to 5 rows via
  // max-h-[170px] + overflow-y-auto on the wrapper div; operator
  // scrolls within that window. Modal height stays compact.
  const filteredDmrUsage = useMemo(() => {
    const q = dmrFilter.trim().toLowerCase();
    if (!q) return dmrUsage.list;
    return dmrUsage.list.filter(
      (p) =>
        String(p.dmrId).includes(q) ||
        p.callsign.toLowerCase().includes(q) ||
        p.displayName.toLowerCase().includes(q),
    );
  }, [dmrUsage.list, dmrFilter]);

  const currentDmrInput = useMemo(() => {
    const raw = values.dmrId.trim();
    if (!/^\d+$/.test(raw)) return 0;
    return Number(raw);
  }, [values.dmrId]);

  const dmrConflictPerson = useMemo(() => {
    if (currentDmrInput <= 0) return null;
    return (
      dmrUsage.list.find(
        (p) => p.dmrId === currentDmrInput && p.id !== initial?.id,
      ) || null
    );
  }, [currentDmrInput, dmrUsage.list, initial]);

  const isOwnDmr =
    !isCreate &&
    initial?.dmrId != null &&
    Number(initial.dmrId) === currentDmrInput;

  function handleNextFreeDmr() {
    const startFrom = currentDmrInput > 0 ? currentDmrInput : DMR_ID_MIN;
    const next = findNextFreeDmrId(dmrUsage.list, startFrom);
    if (next != null) update('dmrId', String(next));
  }

  const errors = useMemo(() => {
    const e = {};
    if (!values.displayName.trim()) e.displayName = 'Name is required.';
    if (values.displayName.length > 128) e.displayName = 'Max 128 characters.';
    if (values.rank && values.rank.length > 32) e.rank = 'Max 32 characters.';
    if (values.position && values.position.length > 128) e.position = 'Max 128 characters.';
    if (values.callsign && values.callsign.length > 64) e.callsign = 'Max 64 characters.';
    // Round 65 — DMR ID. ALWAYS optional in the UI: blank field on
    // create OR edit triggers server-side auto-assign of the lowest
    // free integer. Only non-blank values get range/format checked.
    const raw = values.dmrId.trim();
    if (raw !== '') {
      if (!/^\d+$/.test(raw)) {
        e.dmrId = 'Must be an integer.';
      } else {
        const n = Number(raw);
        if (n < DMR_ID_MIN || n > DMR_ID_MAX) {
          e.dmrId = `Must be ${DMR_ID_MIN}..${DMR_ID_MAX}.`;
        }
      }
    }
    return e;
  }, [values]);

  const canSubmit = Object.keys(errors).length === 0 && !busy;

  function update(field, v) {
    setValues((prev) => ({ ...prev, [field]: v }));
  }

  async function handleSubmit(e) {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const payload = {
        displayName: values.displayName.trim(),
        rank: values.rank.trim() || null,
        position: values.position.trim() || null,
        callsign: values.callsign.trim() || null,
        hasDevice: values.hasDevice,
        homeNodeOnly: values.homeNodeOnly,
      };
      // Round 65 — DMR ID handling:
      //   • non-empty → integer value goes to server (validated above).
      //   • empty on CREATE → omit dmrId entirely; server auto-assigns
      //     the lowest free integer.
      //   • empty on EDIT → send dmrId: null as explicit auto-assign
      //     signal; server treats null as "operator cleared the field,
      //     please reassign the lowest free integer".
      const rawDmr = values.dmrId.trim();
      if (rawDmr !== '') {
        payload.dmrId = Number(rawDmr);
      } else if (!isCreate) {
        payload.dmrId = null;
      }
      await onSave(payload);
    } catch (err) {
      setError(err instanceof ApiError ? err.payload?.error || err.message : err?.message || 'Operation failed.');
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title={isCreate ? `Add personnel to "${unitName || ''}"` : `Edit ${initial?.displayName || 'person'}`}
      description="Personnel record. Toggle 'Has Android device' to mark this person as a terminal owner; the device cert will be issued by the anchor node at first enrollment."
      size="md"
      footer={
        <>
          <button type="button" onClick={onClose} disabled={busy} className="btn-secondary" data-skip-autofocus>
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} disabled={!canSubmit} className="btn-primary">
            {busy ? (
              <>
                <span className="loader-spinner" aria-hidden="true" /> Saving…
              </>
            ) : (
              <>
                <i className={`fa-solid ${isCreate ? 'fa-user-plus' : 'fa-floppy-disk'}`} aria-hidden="true" />
                {isCreate ? 'Add person' : 'Save changes'}
              </>
            )}
          </button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {error && (
          <div role="alert" className="rounded-2xl border border-rose-200 bg-rose-soft px-4 py-3 text-sm text-rose-700">
            <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden="true" />
            {error}
          </div>
        )}

        <div>
          <label htmlFor="p-name" className="field-label">Display name</label>
          <input
            id="p-name"
            className="field"
            value={values.displayName}
            onChange={(e) => update('displayName', e.target.value)}
            autoComplete="off"
            disabled={busy}
            maxLength={128}
          />
          {errors.displayName && <p className="mt-1 text-xs text-rose-600">{errors.displayName}</p>}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="p-rank" className="field-label">Rank <span className="font-normal text-mist">(optional)</span></label>
            <input
              id="p-rank"
              className="field"
              value={values.rank}
              onChange={(e) => update('rank', e.target.value)}
              autoComplete="off"
              disabled={busy}
              maxLength={32}
              placeholder="MAJ, SGT, CPT…"
            />
            {errors.rank && <p className="mt-1 text-xs text-rose-600">{errors.rank}</p>}
          </div>
          <div>
            <label htmlFor="p-callsign" className="field-label">Callsign <span className="font-normal text-mist">(optional)</span></label>
            <input
              id="p-callsign"
              className="field font-mono text-[13px]"
              value={values.callsign}
              onChange={(e) => update('callsign', e.target.value)}
              autoComplete="off"
              disabled={busy}
              maxLength={64}
              placeholder="Eagle-1"
            />
            {errors.callsign && <p className="mt-1 text-xs text-rose-600">{errors.callsign}</p>}
          </div>
        </div>

        <div>
          <label htmlFor="p-dmr-id" className="field-label">
            DMR ID{' '}
            <span className="font-normal text-mist">
              ({DMR_ID_MIN}..{DMR_ID_MAX})
            </span>
          </label>
          <div className="flex gap-2">
            <input
              id="p-dmr-id"
              className="field font-mono text-[13px] flex-1"
              value={values.dmrId}
              onChange={(e) => update('dmrId', e.target.value.replace(/\D/g, ''))}
              autoComplete="off"
              disabled={busy}
              inputMode="numeric"
              maxLength={8}
              placeholder="auto-assigned"
            />
            {/* Round 331 — Next free button. Computes the lowest unused
                DMR ID starting from the current input value (or DMR_ID_MIN
                if input is blank). Fully client-side from the fetched
                usage list — no server round-trip. */}
            <button
              type="button"
              onClick={handleNextFreeDmr}
              disabled={busy || dmrUsage.loading}
              className="btn-secondary text-xs whitespace-nowrap"
              title="Find lowest unused DMR ID"
            >
              <i className="fa-solid fa-magnifying-glass-plus mr-1" aria-hidden="true" />
              Next free
            </button>
          </div>
          {/* Round 331 — inline status line: conflict (red) / own (mist)
              / hint (mist). Mutually exclusive with errors.dmrId; range
              errors stay top priority. */}
          {!errors.dmrId && currentDmrInput > 0 && dmrConflictPerson && (
            <p className="mt-1 text-xs text-rose-600">
              <i className="fa-solid fa-circle-exclamation mr-1" aria-hidden="true" />
              Already used by <span className="font-mono">{dmrConflictPerson.callsign}</span>
            </p>
          )}
          {!errors.dmrId && currentDmrInput > 0 && !dmrConflictPerson && isOwnDmr && (
            <p className="mt-1 text-xs text-mist">
              <i className="fa-solid fa-check mr-1" aria-hidden="true" />
              Current ID for this person
            </p>
          )}
          {!errors.dmrId && currentDmrInput > 0 && !dmrConflictPerson && !isOwnDmr && (
            <p className="mt-1 text-xs text-emerald-700">
              <i className="fa-solid fa-check mr-1" aria-hidden="true" />
              Available
            </p>
          )}
          {/* Round 65 — same hint on create and edit: blank → server
              picks the lowest free integer. On edit, this also lets
              the operator drop the current ID and get a fresh one. */}
          {currentDmrInput === 0 && (
            <p className="mt-1 text-xs text-mist">
              Leave this field blank to automatically receive your DMR ID.
            </p>
          )}
          {errors.dmrId && <p className="mt-1 text-xs text-rose-600">{errors.dmrId}</p>}

          {/* Round 331 — occupied-IDs panel. Hidden when no IDs are
              assigned anywhere (fresh deployment). Otherwise: filter
              input + scrollable table of (DMR ID, Callsign) sorted by
              ID. Click a row → autofill input. Own row (edit mode)
              tinted brand-accent. Conflict row (matches input but
              isn't own) tinted rose. */}
          {dmrUsage.list.length > 0 && (
            <div className="mt-3 rounded-2xl border border-line bg-canvas">
              <div className="flex items-center gap-2 border-b border-line px-3 py-2">
                <i className="fa-solid fa-clipboard-list text-mist text-xs" aria-hidden="true" />
                <span className="text-xs font-medium text-ink">
                  Occupied DMR IDs ({dmrUsage.list.length})
                </span>
                <input
                  type="text"
                  className="ml-auto field h-7 text-xs py-1 w-40"
                  placeholder="Filter by ID or callsign"
                  value={dmrFilter}
                  onChange={(e) => setDmrFilter(e.target.value)}
                  disabled={busy}
                />
              </div>
              {/* Round 331 — visible scroll window = ~5 rows + sticky
                  header. Modal height stays compact + fits screen on
                  small laptops. Scroll within this window for the
                  rest; sticky thead keeps column labels in view. */}
              <div className="max-h-[170px] overflow-y-auto">
                {filteredDmrUsage.length === 0 ? (
                  <div className="px-3 py-3 text-xs text-mist text-center">
                    No matches.
                  </div>
                ) : (
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-canvas">
                      <tr className="text-mist">
                        <th className="px-3 py-1.5 text-left font-medium w-24">DMR ID</th>
                        <th className="px-3 py-1.5 text-left font-medium">Callsign</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDmrUsage.map((p) => {
                        const isSelf = !isCreate && p.id === initial?.id;
                        const isConflict =
                          currentDmrInput === p.dmrId && !isSelf;
                        const rowClass = isSelf
                          ? 'bg-blue-50 hover:bg-blue-100'
                          : isConflict
                          ? 'bg-rose-50 hover:bg-rose-100'
                          : 'hover:bg-mist/10';
                        return (
                          <tr
                            key={p.id}
                            className={`cursor-pointer ${rowClass}`}
                            onClick={() => !busy && update('dmrId', String(p.dmrId))}
                            title={`Click to use ${p.dmrId}`}
                          >
                            <td className="px-3 py-1 font-mono text-ink">
                              {p.dmrId}
                            </td>
                            <td className="px-3 py-1 text-ink">
                              {p.callsign}
                              {isSelf && (
                                <span className="ml-2 text-mist">(this person)</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>

        <div>
          <label htmlFor="p-position" className="field-label">
            Position / role <span className="font-normal text-mist">(optional)</span>
          </label>
          <input
            id="p-position"
            className="field"
            value={values.position}
            onChange={(e) => update('position', e.target.value)}
            autoComplete="off"
            disabled={busy}
            maxLength={128}
            placeholder="Chief of Staff, S2 Officer, Signaller…"
          />
          {errors.position && <p className="mt-1 text-xs text-rose-600">{errors.position}</p>}
        </div>

        <label className="flex items-start gap-3 rounded-2xl border border-line bg-canvas px-4 py-3">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 rounded border-line accent-accent"
            checked={values.hasDevice}
            onChange={(e) => update('hasDevice', e.target.checked)}
            disabled={busy}
          />
          <div>
            <div className="text-sm font-medium text-ink">Has Android device</div>
            <div className="text-xs text-mist">
              Marks this person as a tactical terminal owner. The device certificate is issued automatically
              by the home node at first enrollment (Stage 2 / Stage 3).
            </div>
          </div>
        </label>

        <label className="flex items-start gap-3 rounded-2xl border border-line bg-canvas px-4 py-3">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 rounded border-line accent-warning"
            checked={values.homeNodeOnly}
            onChange={(e) => update('homeNodeOnly', e.target.checked)}
            disabled={busy}
          />
          <div>
            <div className="flex items-center gap-2 text-sm font-medium text-ink">
              <i className="fa-solid fa-lock text-warning text-[11px]" aria-hidden="true" />
              Home node only
            </div>
            <div className="text-xs text-mist">
              When enabled, this person can only connect through the unit's home node — roaming to a foreign
              node is refused at the WebSocket handshake. Default is off (mesh-wide). Requires the unit chain
              to have a home node anchored.
            </div>
          </div>
        </label>
      </form>
    </Modal>
  );
}
